import { useState } from 'react';
import { toast } from 'sonner';
import { ClipboardCheck, Save, AlertCircle, Lock } from 'lucide-react';
import { CHECK_STAGE_LABEL, type PreflightKey } from '@hcc/types';
import { formatDateTime } from '../../lib/dateFmt';
import { Button } from '../ui/Button';
import { api, ApiError } from '../../lib/api';
import type {
  ActiveCheck,
  CheckStatus,
  Preflight,
} from '../../routes/admin/person/types';

const STATUSES: CheckStatus[] = ['ok', 'pending', 'missing', 'na'];

export function PreflightPanel({
  personId,
  metadata,
  checks,
  onSaved,
}: {
  personId: string;
  metadata: Record<string, unknown>;
  checks: ActiveCheck[];
  onSaved: (meta: Record<string, unknown>) => void;
}) {
  const existing = (metadata.preflight ?? {}) as Partial<Preflight>;
  // A gate with an assigned screening check belongs to the checks
  // workflow; the server ignores manual writes to it, so don't offer one.
  const managedBy = (key: PreflightKey) => checks.find((c) => c.preflight_key === key);
  const [dbs, setDbs] = useState<CheckStatus>(existing.dbs_status ?? 'pending');
  const [rtw, setRtw] = useState<CheckStatus>(existing.right_to_work ?? 'pending');
  const [dvla, setDvla] = useState<CheckStatus>(existing.dvla_check ?? 'pending');
  const [p45, setP45] = useState<CheckStatus>(existing.p45_received ?? 'pending');
  const [notes, setNotes] = useState(existing.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const r = await api<{ metadata: Record<string, unknown> }>(
        `/api/admin/people/${personId}/preflight`,
        {
          method: 'PUT',
          body: JSON.stringify({
            dbs_status: dbs,
            right_to_work: rtw,
            dvla_check: dvla,
            p45_received: p45,
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          }),
        },
      );
      toast.success('Pre-employment checklist saved');
      onSaved(r.metadata);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'save failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
      <div className="flex items-center gap-2 border-b border-line/10 px-5 py-3">
        <ClipboardCheck className="h-4 w-4 text-fg-muted" />
        <h2 className="text-sm font-semibold">Pre-Employment Checklist</h2>
      </div>
      <div className="p-5 sm:p-6">
        <p className="text-xs text-fg-muted">
          HR-supplied gate checks. Setting any to <em>missing</em> auto-creates a
          FlagRaised event in the matching category. A gate linked to an
          onboarding task locks to <em>OK</em> automatically when HR approves
          that task.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CheckRow label="DBS" value={dbs} onChange={setDbs} lockedBy={existing.sources?.dbs?.title} managedBy={managedBy('dbs')} />
          <CheckRow label="Right to work" value={rtw} onChange={setRtw} lockedBy={existing.sources?.right_to_work?.title} managedBy={managedBy('right_to_work')} />
          <CheckRow label="DVLA check" value={dvla} onChange={setDvla} lockedBy={existing.sources?.dvla?.title} managedBy={managedBy('dvla')} />
          <CheckRow label="P45 or HMRC Starter Checklist" value={p45} onChange={setP45} lockedBy={existing.sources?.p45?.title} managedBy={managedBy('p45')} />
        </div>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg-muted">
            Checklist notes <span className="font-normal text-fg-dim">(optional)</span>
          </span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1.5 block w-full rounded-md border border-line-strong/20 bg-surface px-3 py-2 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        {existing.updated_at ? (
          <p className="mt-2 text-xs text-fg-muted">
            Last updated {formatDateTime(existing.updated_at)}.
          </p>
        ) : null}
        {error ? (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end border-t border-line/10 bg-surface-2 px-5 py-3">
        <Button
          variant="primary"
          size="md"
          iconLeft={<Save className="h-4 w-4" />}
          loading={saving}
          onClick={save}
        >
          Save checklist
        </Button>
      </div>
    </section>
  );
}

function CheckRow({
  label,
  value,
  onChange,
  lockedBy,
  managedBy,
}: {
  label: string;
  value: CheckStatus;
  onChange: (v: CheckStatus) => void;
  /** Task title when this gate is satisfied (and locked) by onboarding. */
  lockedBy?: string;
  /** The assigned screening check that owns this gate, when one exists. */
  managedBy?: ActiveCheck;
}) {
  const colors: Record<CheckStatus, string> = {
    ok: 'border-success/25 bg-success-soft/15 text-success-soft-fg',
    pending: 'border-warning/25 bg-warning-soft/15 text-warning-soft-fg',
    missing: 'border-danger/25 bg-danger-soft/15 text-danger-soft-fg',
    na: 'border-line-strong/20 bg-surface-2 text-fg-muted',
  };

  // Onboarding-driven gate: read-only OK with the source task.
  if (lockedBy) {
    return (
      <div className="block text-sm">
        <span className="font-medium text-fg-muted">{label}</span>
        <div className={`mt-1.5 flex h-10 w-full items-center gap-2 rounded-md border px-3 text-sm font-medium ${colors.ok}`}>
          <Lock className="h-3.5 w-3.5 shrink-0" />
          OK
        </div>
        <span className="mt-1 block text-xs text-fg-dim">via onboarding: {lockedBy}</span>
      </div>
    );
  }

  // Mid-workflow screening check: the gate only completes via the
  // physical verification on the checks page, never by hand here.
  if (managedBy) {
    return (
      <div className="block text-sm">
        <span className="font-medium text-fg-muted">{label}</span>
        <div className={`mt-1.5 flex h-10 w-full items-center gap-2 rounded-md border px-3 text-sm font-medium ${colors[value]}`}>
          <Lock className="h-3.5 w-3.5 shrink-0" />
          {CHECK_STAGE_LABEL[managedBy.stage]}
        </div>
        <span className="mt-1 block text-xs text-fg-dim">
          Managed by the {managedBy.title} task; complete it from the checks page.
        </span>
      </div>
    );
  }

  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CheckStatus)}
        className={`mt-1.5 block h-10 w-full rounded-md border px-3 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-line/15 ${colors[value]}`}
      >
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {labelFor(s)}
          </option>
        ))}
      </select>
    </label>
  );
}

function labelFor(s: CheckStatus): string {
  if (s === 'ok') return 'OK';
  if (s === 'pending') return 'Pending';
  if (s === 'missing') return 'Missing';
  return 'N/A';
}
